// API Interceptor for automatic token refresh and error handling

import { useAuthStore } from '@/stores/auth-store';
import { tokenUtils } from '@/utils/token-utils';
import authApi from '@/services/auth/api';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Helper to get tokens from auth store (works outside React components)
function getTokens() {
  return useAuthStore.getState().tokens;
}

// Helper to set tokens in auth store
function setTokens(tokens: { accessToken: string; refreshToken: string } | null) {
  useAuthStore.getState().setTokens(tokens);
}

// Helper to clear auth
function clearAuth() {
  useAuthStore.getState().logout();
}

// Subscribe to token refresh
function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// Notify all subscribers with new token
function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Clear all pending subscribers on refresh failure
function onRefreshFailed() {
  refreshSubscribers = [];
}

// Enhanced fetch with authentication and automatic retry
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const tokens = getTokens();

  // Add auth header if token exists
  if (tokens?.accessToken) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${tokens.accessToken}`,
    };
  }

  // Add default headers. Skip the JSON Content-Type for FormData bodies so the
  // browser can set `multipart/form-data; boundary=...` itself (FR-3 uploads).
  const isFormData =
    typeof FormData !== 'undefined' && options.body instanceof FormData;
  options.headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  // Add credentials for CORS
  options.credentials = 'include';

  let response = await fetch(url, options);

  // Handle 401 Unauthorized - try to refresh token
  if (response.status === 401 && tokens?.refreshToken) {
    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const refreshResponse = await authApi.refreshToken({
          refresh_token: tokens.refreshToken,
        });

        if (refreshResponse.success) {
          const newTokens = {
            accessToken: refreshResponse.access_token,
            refreshToken: refreshResponse.refresh_token,
          };

          setTokens(newTokens);
          onRefreshed(newTokens.accessToken);

          // Retry original request with new token
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${newTokens.accessToken}`,
          };
          response = await fetch(url, options);
        } else {
          // Refresh failed, clear auth and redirect to login
          onRefreshFailed();
          clearAuth();
          window.location.href = '/login';
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        onRefreshFailed();
        clearAuth();
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    } else {
      // Wait for token refresh to complete
      return new Promise((resolve) => {
        subscribeTokenRefresh((token: string) => {
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          };
          resolve(fetch(url, options));
        });
      });
    }
  }

  return response;
}

const ACCOUNT_SUSPENDED_PATH = '/account-suspended';

export function redirectToAccountSuspended(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === ACCOUNT_SUSPENDED_PATH) return;
  window.location.assign(ACCOUNT_SUSPENDED_PATH);
}

// Generic API request wrapper
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await authenticatedFetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));

      // A suspended or closed tenant answers 403 TENANT_SUSPENDED on every call. Send the
      // user to the one page that explains it instead of letting each screen show a wall
      // of failed queries.
      if (response.status === 403 && errorData?.errorCode === 'TENANT_SUSPENDED') {
        redirectToAccountSuspended();
      }

      // Extract error message from the API response. `message` carries the specific
      // reason; `error` is often only a category label ("Business Error"), so it is
      // the fallback, not the first choice. Bean-validation responses put the useful
      // text in `fieldErrors`.
      const fieldErrorText = errorData?.fieldErrors && typeof errorData.fieldErrors === 'object'
        ? Object.values(errorData.fieldErrors as Record<string, string>).join(' ')
        : '';
      const errorMessage =
        fieldErrorText ||
        errorData.message ||
        errorData.error ||
        errorData.detail ||
        'An error occurred';

      throw {
        message: errorMessage,
        status: response.status,
        code: errorData.errorCode || errorData.code,
        // Preserve the original response for detailed error handling
        response: errorData,
      };
    }

    // Handle empty responses
    const text = await response.text();
    return text ? JSON.parse(text) : {} as T;
  } catch (error) {
    // Suppress console noise for 404s — callers handle these as expected "not found" responses
    if ((error as { status?: number }).status !== 404) {
      console.error('API request error:', error);
    }
    throw error;
  }
}

// Check and refresh token if needed before making requests
export async function checkAndRefreshToken(): Promise<void> {
  const tokens = getTokens();

  if (!tokens) return;

  if (tokenUtils.isTokenExpired(tokens.accessToken)) {
    // Token is expired, try to refresh
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const refreshResponse = await authApi.refreshToken({
          refresh_token: tokens.refreshToken,
        });

        if (refreshResponse.success) {
          const newTokens = {
            accessToken: refreshResponse.access_token,
            refreshToken: refreshResponse.refresh_token,
          };
          setTokens(newTokens);
          onRefreshed(newTokens.accessToken);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearAuth();
        throw error;
      } finally {
        isRefreshing = false;
      }
    }
  } else if (tokenUtils.needsRefresh(tokens.accessToken)) {
    // Token needs refresh soon, do it in background (suppress 401 errors)
    authApi.refreshToken({
      refresh_token: tokens.refreshToken,
    }).then((response) => {
      if (response.success) {
        setTokens({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        });
      }
    }).catch(() => {
      // Silently ignore background refresh failures — the 401 interceptor
      // will handle it when the token actually expires on a real request.
    });
  }
}

// Validate tokens on app startup by refreshing with the server.
// Always validates regardless of client-side JWT expiry because tokens may be
// invalidated server-side (key rotation, session revocation, server restart).
// The cost is one lightweight refresh call (~50-100ms) per page load, which
// also keeps tokens fresh and eliminates all spurious 401 errors.
// Includes a timeout so users aren't stuck on a spinner if the server is down.
const STARTUP_VALIDATION_TIMEOUT_MS = 8000;

export async function validateTokensOnStartup(): Promise<void> {
  const tokens = getTokens();

  if (!tokens?.refreshToken) {
    throw new Error('No refresh token available');
  }

  const refreshPromise = (async () => {
    // Wait for any in-flight refresh to complete instead of skipping silently
    if (isRefreshing) {
      return new Promise<void>((resolve) => {
        subscribeTokenRefresh(() => resolve());
        // If the in-flight refresh fails, onRefreshFailed clears subscribers,
        // so we also need a fallback timeout handled by the outer race.
      });
    }

    isRefreshing = true;
    try {
      const refreshResponse = await authApi.refreshToken({
        refresh_token: tokens.refreshToken,
      });

      if (refreshResponse.success) {
        const newTokens = {
          accessToken: refreshResponse.access_token,
          refreshToken: refreshResponse.refresh_token,
        };
        setTokens(newTokens);
        onRefreshed(newTokens.accessToken);
      } else {
        throw new Error('Token validation failed');
      }
    } catch (error) {
      onRefreshFailed();
      throw error;
    } finally {
      isRefreshing = false;
    }
  })();

  // Race the refresh against a timeout so users don't wait forever
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Token validation timed out')), STARTUP_VALIDATION_TIMEOUT_MS)
  );

  await Promise.race([refreshPromise, timeoutPromise]);
}

// Set up periodic token check (every 30 seconds)
// Returns a cleanup function to clear the interval
export function setupTokenRefreshInterval(): () => void {
  const intervalId = setInterval(() => {
    checkAndRefreshToken().catch(console.error);
  }, 30000);

  return () => clearInterval(intervalId);
}

export default {
  authenticatedFetch,
  apiRequest,
  checkAndRefreshToken,
  setupTokenRefreshInterval,
};
